import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Slider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { API_URL } from '../utils/config';

interface ImproveMessageModalProps {
  open: boolean;
  onClose: () => void;
  messageId: string;
  messageContent: string;
}

const categories = [
  { value: 'market_terminology', label: 'Market Terminology' },
  { value: 'data_interpretation', label: 'Data Interpretation' },
  { value: 'tool_usage', label: 'Tool Usage' },
  { value: 'corrections', label: 'Corrections' },
  { value: 'general_advice', label: 'General Advice' },
  { value: 'best_practices', label: 'Best Practices' },
  { value: 'error_handling', label: 'Error Handling' },
];

export const ImproveMessageModal: React.FC<ImproveMessageModalProps> = ({
  open,
  onClose,
  messageId,
  messageContent,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [category, setCategory] = useState('corrections');
  const [topic, setTopic] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!topic.trim() || !content.trim()) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/mobile/admin/knowledge/from-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatMessageId: messageId,
          category,
          topic: topic.trim(),
          content: content.trim(),
          priority,
        }),
      });

      if (!response || !response.ok) {
        throw new Error('Failed to create knowledge entry');
      }

      enqueueSnackbar('Knowledge entry created successfully', { variant: 'success' });

      // Reset form
      setCategory('corrections');
      setTopic('');
      setContent('');
      setPriority(5);

      onClose();
    } catch (error: any) {
      console.error('Error creating knowledge entry:', error);
      enqueueSnackbar(error.message || 'Failed to create knowledge entry', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, fontWeight: 'bold' }}>
        Improve Mia's Knowledge
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Provide corrections or advice to help Mia improve future responses. This knowledge will be
          permanently stored and used in all future conversations.
        </Typography>

        <Box
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            maxHeight: '150px',
            overflow: 'auto',
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Original Message:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
            {messageContent.substring(0, 500)}
            {messageContent.length > 500 && '...'}
          </Typography>
        </Box>

        <TextField
          fullWidth
          select
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          margin="normal"
          required
        >
          {categories.map((cat) => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Topic"
          placeholder="Brief title for this knowledge entry"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="Correction or Advice"
          placeholder="Describe the correction or advice to improve Mia's responses"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          margin="normal"
          multiline
          rows={4}
          required
        />

        <Box sx={{ mt: 3, mb: 1 }}>
          <Typography variant="body2" gutterBottom>
            Priority
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Higher priority knowledge is emphasized more in Mia's responses (1-10)
          </Typography>
          <Slider
            value={priority}
            onChange={(_, value) => setPriority(value as number)}
            min={1}
            max={10}
            step={1}
            marks
            valueLabelDisplay="on"
            sx={{
              color: '#F5A800',
              '& .MuiSlider-markLabel': {
                fontSize: '0.7rem',
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting} sx={{ color: '#607D8B' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !topic.trim() || !content.trim()}
          sx={{
            bgcolor: '#F5A800',
            color: '#FFFFFF',
            '&:hover': {
              bgcolor: '#D89500',
            },
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save Knowledge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
